import { useContext } from 'react'
import { NavLink } from 'react-router-dom'
import { SolutionOutlined } from '@ant-design/icons'
import {
  isModeBup,
  isWebinars,
  isSociogram,
  isData,
  hasCourseAssignmentEnabled
} from '@BUP/services/BraveUp'
import { useTranslation } from 'react-i18next'
import styles from './student-navigation.module.scss'
import classNames from 'classnames'
import IconMenu from '@BUP/components/IconMenu'
import Rbac from '@BUP/components/RBAC'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { LocationContext } from '@BUP/contexts/LocationContext'
import { IsParticipantContext } from '@BUP/contexts/IsParticipantContext'

const StudentNavigation = () => {
  const organization = useContext(OrganizationContext)
  const locale = useContext(LocationContext)
  const organizationCountry = locale && locale.country
  const { isParticipant } = useContext(IsParticipantContext)
  const [t] = useTranslation()
  const getNavLinkClassName = ({ isActive }) =>
    classNames(styles.nav_link, { [styles.active]: isActive, active: isActive })
  const getSubNavLinkClassName = ({ isActive }) =>
    classNames(styles.sub_nav_link, { [styles.active]: isActive, active: isActive })

  return (
    <nav className={styles.navigation_wrapper}>
      <div className={styles.links_wrapper}>
        <div className={styles.element_wrapper}>
          <NavLink
            to='/'
            end
            className={getNavLinkClassName}
          >
            <IconMenu iconName='home' />
            <span className={styles.nav_label}>{t('nav.home')}</span>
            <span className={styles.nav_label_mobile}>
              {t('nav.homeMobile')}
            </span>
          </NavLink>
        </div>
        <div className={styles.element_wrapper}>
          <NavLink
            to='/resources'
            className={getNavLinkClassName}
          >
            <IconMenu iconName='resources' />
            <span className={styles.nav_label}>{t('nav.resources')}</span>
            <span className={styles.nav_label_mobile}>
              {t('nav.resourcesMobile')}
            </span>
          </NavLink>
        </div>
        {isWebinars(organization)
          ? (
            <div className={styles.element_wrapper}>
              <Rbac name='navWebinars'>
                <NavLink
                  to='/webinars'
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='webinar' />
                  <span className={styles.nav_label}>{t('nav.webinars')}</span>
                </NavLink>
              </Rbac>
            </div>
            )
          : null}
        {isModeBup(organization)
          ? (
            <>
              <div
                className={
                styles.element_wrapper + ' ' + styles.element_removable
              }
              >
                <NavLink
                  to='/modebup'
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='modoBUP' />
                  <span className={styles.nav_label}>{t('nav.bupmode')}</span>
                  <span className={styles.nav_label_mobile}>
                    {t('nav.bupmodeMobile')}
                  </span>
                </NavLink>
                <Rbac name='navModeBupSendLink'>
                  <NavLink
                    to='/modebup/send'
                    className={getSubNavLinkClassName}
                  >
                    <span className={styles.nav_label}>
                      {t('nav.sendMessage')}
                    </span>
                  </NavLink>
                </Rbac>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <NavLink
                    to='/modebup/inbox'
                    className={getSubNavLinkClassName}
                  >
                    <span className={styles.nav_label}>{t('nav.inbox')}</span>
                  </NavLink>
                </div>
                <Rbac name='navModeBupArchivedLink'>
                  <NavLink
                    to='/modebup/archived'
                    className={getSubNavLinkClassName}
                  >
                    <span className={styles.nav_label}>
                      {t('nav.archivedCases')}
                    </span>
                  </NavLink>
                </Rbac>
              </div>
              <div
                className={
                styles.element_wrapper + ' ' + styles.element_mobile_only
              }
              >
                <NavLink
                  to='/modebup'
                  end
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='modoBUP' />
                  <span className={styles.nav_label_mobile}>
                    {t('nav.bupmodeMobile')}
                  </span>
                </NavLink>
              </div>
              <div
                className={
                styles.element_wrapper + ' ' + styles.element_mobile_only
              }
              >
                <NavLink
                  to='/modebup/inbox'
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='inbox' />
                  <span className={styles.nav_label}>{t('nav.inbox')}</span>
                  <span className={styles.nav_label_mobile}>
                    {t('nav.inboxMobile')}
                  </span>
                </NavLink>
              </div>
            </>
            )
          : null}
        <div
          className={styles.element_wrapper}
          style={{ marginBottom: 25 }}
        >
          {organizationCountry === 'Spain'
            ? null
            : (
              <NavLink
                to='/data'
                className={getNavLinkClassName}
              >
                <IconMenu iconName='data' />
                <span className={styles.nav_label}>{t('nav.data')}</span>
                <span className={styles.nav_label_mobile}>
                  {t('nav.dataMobile')}
                </span>
              </NavLink>
              )}
          {isData(organization) && organizationCountry !== 'Spain' && (
            <Rbac name='navDiagnostic'>
              <NavLink
                to='/data/diagnostic'
                className={getSubNavLinkClassName}
              >
                <span className={styles.nav_label}>{t('nav.diagnostic')}</span>
              </NavLink>
            </Rbac>
          )}
          {isSociogram(organization) &&
          organizationCountry !== 'Spain' &&
          isParticipant
            ? (
              <Rbac name='navSociogram'>
                <NavLink
                  to='/sociogram/start-survey'
                  className={getSubNavLinkClassName}
                >
                  <span className={styles.nav_label}>{t('nav.sociogram')}</span>
                </NavLink>
              </Rbac>
              )
            : null}
          {hasCourseAssignmentEnabled(organization) && (
            <Rbac name='navCourseAssignment'>
              <NavLink
                to='/course-assignment/preferences'
                className={getSubNavLinkClassName}
              >
                <span className={styles.nav_label}>{t('nav.courseAssignment')}</span>
              </NavLink>
            </Rbac>
          )}
        </div>
        {hasCourseAssignmentEnabled(organization) && (
          <div
            className={
            styles.element_wrapper + ' ' + styles.element_mobile_only
          }
          >
            <Rbac name='navCourseAssignment'>
              <NavLink
                to='/course-assignment/preferences'
                className={getNavLinkClassName}
              >
                <SolutionOutlined className={classNames('menu_icon', styles.nav_icon_antd)} />
                <span className={styles.nav_label}>{t('nav.courseAssignment')}</span>
                <span className={styles.nav_label_mobile}>
                  {t('nav.courseAssignmentMobile')}
                </span>
              </NavLink>
            </Rbac>
          </div>
        )}
      </div>
    </nav>
  )
}

export default StudentNavigation
